import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { SettingsService } from '../../../shared/services/settingsService';

// ─── helpers ────────────────────────────────────────────────────────────────

const TYPES = ['boolean', 'number', 'string', 'email[]'];
const CATEGORIES = ['Email', 'Stock', 'Scheduler', 'General'];

function parseValue(type, raw) {
  try {
    if (type === 'boolean') return JSON.parse(raw) === true;
    if (type === 'number') return Number(JSON.parse(raw));
    if (type === 'email[]') return JSON.parse(raw);
  } catch {}
  return raw;
}

function serializeValue(type, input) {
  if (type === 'boolean') return JSON.stringify(Boolean(input));
  if (type === 'number') return JSON.stringify(Number(input));
  if (type === 'email[]') {
    const arr = String(input)
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    return JSON.stringify(arr);
  }
  return String(input);
}

function displayValue(type, raw) {
  const parsed = parseValue(type, raw);
  if (type === 'boolean') return parsed ? 'Enabled' : 'Disabled';
  if (type === 'email[]') return Array.isArray(parsed) && parsed.length ? parsed.join(', ') : '—';
  return String(parsed ?? '—');
}

function getEmailsFromText(value) {
  if (Array.isArray(value)) return value;
  return value
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
}

function normalizeEmailList(value) {
  if (Array.isArray(value)) return value.join(', ');
  return value ?? '';
}

function normalizeCenters(payload = {}) {
  const centers =
    payload.data?.centers ?? payload.data?.data?.centers ?? payload.centers ?? payload.data ?? payload;
  if (!Array.isArray(centers)) return [];
  return centers.map((c) => ({
    id: c.id ?? c._id ?? c.centerId,
    name: c.name ?? c.centerName ?? 'Unnamed center',
    code: c.code ?? c.centerCode ?? '',
    lowStockAlertEmails: normalizeEmailList(
      c.lowStockAlertEmails ?? c.emailNotificationRecipients ?? c.lowStockRecipients
    ),
  }));
}

const EMPTY_FORM = { key: '', value: '', type: 'boolean', label: '', description: '', category: 'General' };

// ─── sub-components ──────────────────────────────────────────────────────────

const Toast = ({ toast }) => {
  if (!toast) return null;
  const ok = toast.type === 'success';
  return (
    <div
      className={`fixed right-4 top-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
        ok ? 'bg-green-600' : 'bg-red-600'
      }`}
    >
      {ok ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationCircleIcon className="h-5 w-5" />}
      {toast.message}
    </div>
  );
};

const Toggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? 'bg-[#4057fe]' : 'bg-gray-300 dark:bg-gray-600'
    }`}
    aria-pressed={checked}
  >
    <span
      className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-4' : 'translate-x-0'
      }`}
    />
  </button>
);

// ─── modal ───────────────────────────────────────────────────────────────────

function SettingModal({ mode, initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(
    mode === 'edit'
      ? {
          ...initial,
          value:
            initial.type === 'email[]'
              ? (parseValue('email[]', initial.value) ?? []).join(', ')
              : String(parseValue(initial.type, initial.value) ?? ''),
        }
      : { ...EMPTY_FORM }
  );
  const [errors, setErrors] = useState({});

  const set = (field, val) => {
    setForm((f) => ({ ...f, [field]: val }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = 'Label is required.';
    if (mode === 'add') {
      if (!form.key.trim()) e.key = 'Key is required.';
      else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(form.key))
        e.key = 'Key must start with a letter and contain only letters, numbers, or underscores.';
    }
    if (form.type === 'number' && isNaN(Number(form.value))) e.value = 'Must be a number.';
    if (form.type === 'email[]') {
      const emails = form.value.split(',').map((e) => e.trim()).filter(Boolean);
      const bad = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      if (bad.length) e.value = `Invalid email(s): ${bad.join(', ')}`;
    }
    return e;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({
      ...form,
      value: serializeValue(form.type, form.value),
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {mode === 'add' ? 'Add Setting' : 'Edit Setting'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {/* Key — readonly in edit */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Key</label>
            <input
              value={form.key}
              readOnly={mode === 'edit'}
              onChange={(e) => set('key', e.target.value)}
              placeholder="e.g. myFeatureEnabled"
              className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:bg-gray-700 dark:text-white ${
                mode === 'edit'
                  ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-600 dark:bg-gray-700/50'
                  : 'border-gray-200 bg-gray-50 dark:border-gray-600'
              }`}
            />
            {errors.key && <p className="mt-1 text-xs text-red-500">{errors.key}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Label</label>
            <input
              value={form.label}
              onChange={(e) => set('label', e.target.value)}
              placeholder="Display name"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            {errors.label && <p className="mt-1 text-xs text-red-500">{errors.label}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
              <select
                value={form.type}
                disabled={mode === 'edit'}
                onChange={(e) => { set('type', e.target.value); set('value', ''); }}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] dark:border-gray-600 dark:bg-gray-700 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
              <input
                list="category-options"
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <datalist id="category-options">
                {CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Value field — type-aware */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Value</label>
            {form.type === 'boolean' ? (
              <div className="flex items-center gap-3 py-1">
                <Toggle checked={form.value === 'true' || form.value === true} disabled={false} onChange={(v) => set('value', v ? 'true' : 'false')} />
                <span className="text-sm text-gray-600 dark:text-gray-300">{form.value === 'true' || form.value === true ? 'Enabled' : 'Disabled'}</span>
              </div>
            ) : form.type === 'number' ? (
              <input
                type="number"
                value={form.value}
                onChange={(e) => set('value', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            ) : form.type === 'email[]' ? (
              <textarea
                rows={3}
                value={form.value}
                onChange={(e) => set('value', e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            ) : (
              <input
                value={form.value}
                onChange={(e) => set('value', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            )}
            {errors.value && <p className="mt-1 text-xs text-red-500">{errors.value}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#4057fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#3347e0] disabled:opacity-50"
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {mode === 'add' ? 'Add Setting' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function GeneralSettingsPage() {
  const navigate = useNavigate();
  const [appSettings, setAppSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', setting?: {} }
  const [modalSaving, setModalSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // key
  const [centers, setCenters] = useState([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const grouped = useMemo(() => {
    const map = {};
    for (const s of appSettings) {
      if (!map[s.category]) map[s.category] = [];
      map[s.category].push(s);
    }
    return map;
  }, [appSettings]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    setLoading(true);
    setCentersLoading(true);
    setLoadError('');
    try {
      const [settingsRes, centersRes] = await Promise.all([
        SettingsService.getAppSettings(),
        SettingsService.getCenterNotificationEmails(),
      ]);
      setAppSettings(settingsRes.data?.data ?? []);
      setCenters(normalizeCenters(centersRes.data));
    } catch (err) {
      setLoadError(err?.response?.data?.message ?? 'Failed to load settings.');
    } finally {
      setLoading(false);
      setCentersLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Instant toggle for boolean settings
  const handleToggle = async (key, current) => {
    const newVal = JSON.stringify(!current);
    setAppSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: newVal } : s)));
    try {
      await SettingsService.updateAppSetting(key, { value: newVal });
    } catch (err) {
      setAppSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value: JSON.stringify(current) } : s)));
      showToast(err?.response?.data?.message ?? 'Failed to update setting.', 'error');
    }
  };

  const handleModalSave = async (formData) => {
    setModalSaving(true);
    try {
      if (modal.mode === 'add') {
        const res = await SettingsService.createAppSetting(formData);
        setAppSettings((prev) => [...prev, res.data.data]);
        showToast('Setting added.');
      } else {
        const { value, label, description, category } = formData;
        const res = await SettingsService.updateAppSetting(modal.setting.key, { value, label, description, category });
        setAppSettings((prev) => prev.map((s) => (s.key === modal.setting.key ? res.data.data : s)));
        showToast('Setting updated.');
      }
      setModal(null);
    } catch (err) {
      showToast(err?.response?.data?.message ?? 'Failed to save setting.', 'error');
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      await SettingsService.deleteAppSetting(key);
      setAppSettings((prev) => prev.filter((s) => s.key !== key));
      showToast('Setting deleted.');
    } catch (err) {
      showToast(err?.response?.data?.message ?? 'Failed to delete setting.', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleTestEmail = async () => {
    const notifSetting = appSettings.find((s) => s.key === 'notificationEmailEnabled');
    if (!notifSetting || !parseValue('boolean', notifSetting.value)) {
      showToast('Notification email is disabled.', 'error');
      return;
    }
    setTesting(true);
    try {
      await SettingsService.sendLowStockTestEmail({});
      showToast('Test email sent.');
    } catch (err) {
      showToast(err?.response?.data?.message ?? 'Failed to send test email.', 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <Toast toast={toast} />

      {modal && (
        <SettingModal
          mode={modal.mode}
          initial={modal.setting}
          onSave={handleModalSave}
          onClose={() => setModal(null)}
          saving={modalSaving}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
            <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-white">Delete Setting</h2>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              Are you sure you want to delete <span className="font-mono font-medium text-gray-800 dark:text-gray-200">{deleteConfirm}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#4057fe]">
              <Cog6ToothIcon className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">General Settings</h1>
          </div>
          <p className="ml-12 mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage application settings dynamically
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setModal({ mode: 'add' })}
            className="inline-flex items-center gap-2 rounded-lg bg-[#4057fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#3347e0]"
          >
            <PlusIcon className="h-4 w-4" />
            Add Setting
          </button>
        </div>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {loadError}
        </div>
      ) : loading ? (
        <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">Loading settings…</div>
      ) : (
        <div className="space-y-5">
          {/* Dynamic settings grouped by category */}
          {Object.keys(grouped).sort().map((category) => (
            <div
              key={category}
              className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-700">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {category}
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {grouped[category].map((setting) => {
                  const isBoolean = setting.type === 'boolean';
                  const parsed = parseValue(setting.type, setting.value);
                  return (
                    <div key={setting.key} className="flex items-start justify-between gap-4 px-5 py-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {setting.label}
                          </span>
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            {setting.key}
                          </span>
                          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                            {setting.type}
                          </span>
                        </div>
                        {setting.description && (
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{setting.description}</p>
                        )}
                        {!isBoolean && (
                          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                            {displayValue(setting.type, setting.value)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        {isBoolean && (
                          <Toggle
                            checked={parsed === true}
                            disabled={false}
                            onChange={(v) => handleToggle(setting.key, parsed)}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setModal({ mode: 'edit', setting })}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(setting.key)}
                          className="rounded-lg border border-red-100 p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 dark:border-red-900/40 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {appSettings.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              No settings yet.{' '}
              <button
                type="button"
                onClick={() => setModal({ mode: 'add' })}
                className="font-medium text-[#4057fe] hover:underline"
              >
                Add the first one
              </button>
            </div>
          )}

          {/* Child Plan Notifications */}
          {(() => {
            const enabledSetting = appSettings.find(s => s.key === 'childPlanNotificationEnabled');
            const intervalSetting = appSettings.find(s => s.key === 'childPlanOverdueReminderDays');
            return (
              <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-700 flex items-center gap-2">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-[#4057fe]" />
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Child Plan Notifications
                  </h2>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {/* Toggle: enable age-based vaccination reminders */}
                  <div className="flex items-start justify-between gap-4 px-5 py-4">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        Enable age-based vaccination reminders
                      </span>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Send reminders to parents when a child's vaccination is due based on their age schedule.
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {enabledSetting ? (
                        <>
                          <Toggle
                            checked={parseValue('boolean', enabledSetting.value) === true}
                            disabled={false}
                            onChange={() => handleToggle(enabledSetting.key, parseValue('boolean', enabledSetting.value))}
                          />
                          <button
                            type="button"
                            onClick={() => setModal({ mode: 'edit', setting: enabledSetting })}
                            className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setModal({ mode: 'add', setting: { key: 'childPlanNotificationEnabled', label: 'Enable age-based vaccination reminders', type: 'boolean', value: 'false', category: 'Scheduler', description: 'Send reminders to parents when a vaccination is due based on child age schedule.' } })}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:border-[#4057fe] hover:text-[#4057fe] dark:border-gray-600 dark:text-gray-400"
                        >
                          <PlusIcon className="h-3.5 w-3.5" /> Add setting
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Number input: overdue reminder repeat interval */}
                  <div className="flex items-start justify-between gap-4 px-5 py-4">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        Overdue reminder repeat interval (days)
                      </span>
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        How often to re-send overdue reminders. Set to 0 to send only once.
                      </p>
                      {intervalSetting && (
                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                          {displayValue('number', intervalSetting.value)} {parseValue('number', intervalSetting?.value) === 1 ? 'day' : 'days'}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {intervalSetting ? (
                        <button
                          type="button"
                          onClick={() => setModal({ mode: 'edit', setting: intervalSetting })}
                          className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setModal({ mode: 'add', setting: { key: 'childPlanOverdueReminderDays', label: 'Overdue reminder repeat interval (days)', type: 'number', value: '0', category: 'Scheduler', description: 'Interval in days to re-send overdue reminders. 0 = send once.' } })}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:border-[#4057fe] hover:text-[#4057fe] dark:border-gray-600 dark:text-gray-400"
                        >
                          <PlusIcon className="h-3.5 w-3.5" /> Add setting
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Center Notification Recipients */}
          <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-[#4057fe]" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Center Notification Recipients</h2>
                <div className="group relative">
                  <InformationCircleIcon className="h-5 w-5 cursor-help text-gray-400" />
                  <div className="pointer-events-none absolute left-1/2 top-7 z-20 hidden w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-medium text-white shadow-lg group-hover:block">
                    Recipients are managed from each center editor. Use Edit Center to add or update emails.
                  </div>
                </div>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Display only</span>
            </div>

            {centersLoading ? (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading centers…</div>
            ) : centers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                No centers found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/60">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">Center</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">Recipients</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">Manage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {centers.map((center) => (
                      <tr key={center.id}>
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-gray-900 dark:text-white">{center.name}</div>
                          {center.code && <div className="text-xs text-gray-400">{center.code}</div>}
                        </td>
                        <td className="px-3 py-3">
                          {getEmailsFromText(center.lowStockAlertEmails).length > 0 ? (
                            <div className="flex min-w-[280px] flex-wrap gap-2">
                              {getEmailsFromText(center.lowStockAlertEmails).map((email) => (
                                <span
                                  key={email}
                                  className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-200"
                                >
                                  {email}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No recipients added</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right align-top">
                          <button
                            type="button"
                            onClick={() => navigate(`/admin/centers/edit/${center.id}`)}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#4057fe] px-3 py-2 text-xs font-medium text-white hover:bg-[#3347e0]"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit Center
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Test email */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleTestEmail}
              disabled={testing}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {testing ? 'Sending…' : 'Send Test Email'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
